import DeleteIcon from '../DeleteIcon';
import { OrcamentoDialog, OrcamentoIcon, useOrcamentoFeedback } from '../OrcamentoUI';
import { useMaquetesTheme } from '../../context/MaquetesThemeContext';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './CompanySettingsModal.css';

const CompanySettingsModal = ({ onClose }) => {
    const { confirm, notify } = useOrcamentoFeedback();
    const { theme } = useMaquetesTheme();
    const [fichasTecnicas, setFichasTecnicas] = useState([]);
    const [contactosUteis, setContactosUteis] = useState([]);
    const [novoContacto, setNovoContacto] = useState({ nome: '', cargo: '', telefone: '', email: '' });
    const [editingContacto, setEditingContacto] = useState(null);
    const [formData, setFormData] = useState({
        nome_empresa: '',
        alvara: '',
        nif: '',
        morada: '',
        seguro_at_seguradora: '',
        seguro_at_apolice: '',
        seguro_at_validade: '',
        seguro_rc_seguradora: '',
        seguro_rc_apolice: '',
        seguro_rc_validade: '',
        financas_data_emissao: '',
        financas_validade: '',
        ss_data_emissao: '',
        ss_validade: '',
        email_alertas: ''
    });
    const [showEmailPass, setShowEmailPass] = useState(false);
    const [documentosInternos, setDocumentosInternos] = useState([]);
    const [loading, setLoading] = useState(true);

    const AVAILABLE_DOCS = [
        'Código de Conduta',
        'Regulamento Interno',
        'Política de Privacidade',
        'Manual de Acolhimento e Segurança',
        'Ficha de Entrega de EPIs',
        'Declaração de Isenção de Horário (se aplicável)',
        'Acordo de Confidencialidade',
        'Manual de Gestão',
        'Comunicação de Perigos e Riscos aos Trabalhadores',
        'Regras Procedimentos em Postos de Combustível',
        'Ficha de Procedimentos de Segurança',
        'Registos de Formação/Ação de Acolhimento'
    ];

    useEffect(() => {
        loadData();
        fetchDocumentosInternos();
        fetchFichasTecnicas();
        fetchContactosUteis();
    }, []);

    const fetchDocumentosInternos = async () => {
        try {
            const res = await api.get('/trabalhadores/documentos-internos');
            setDocumentosInternos(res.data);
        } catch (error) {
            console.error('Erro ao carregar documentos internos:', error);
        }
    };

    const fetchFichasTecnicas = async () => {
        try {
            const res = await api.get('/fichas-tecnicas');
            setFichasTecnicas(res.data);
        } catch (err) {
            console.error('Erro ao carregar fichas técnicas:', err);
        }
    };

    const fetchContactosUteis = async () => {
        try {
            const res = await api.get('/contactos-uteis');
            setContactosUteis(res.data);
        } catch (err) {
            console.error('Erro ao carregar contactos úteis:', err);
        }
    };

    const handleDeleteFichaTecnica = async (id, nome) => {
        if (!await confirm(`Eliminar "${nome}"?`)) return;
        try {
            await api.delete(`/fichas-tecnicas/${id}`);
            fetchFichasTecnicas();
        } catch (err) {
            notify('Erro ao eliminar ficha técnica.');
        }
    };

    const handleDownloadFichaTecnica = async (id, nome) => {
        try {
            const res = await api.get(`/fichas-tecnicas/${id}/pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${nome.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            notify('Erro ao baixar ficha técnica.');
        }
    };

    const handleSaveContacto = async () => {
        if (!novoContacto.nome) return notify('Nome obrigatório.');
        try {
            await api.post('/contactos-uteis', novoContacto);
            setNovoContacto({ nome: '', cargo: '', telefone: '', email: '' });
            fetchContactosUteis();
        } catch (err) {
            notify('Erro ao guardar contacto.');
        }
    };

    const handleUpdateContacto = async () => {
        if (!editingContacto?.nome) return notify('Nome obrigatório.');
        try {
            await api.put(`/contactos-uteis/${editingContacto.id}`, editingContacto);
            setEditingContacto(null);
            fetchContactosUteis();
        } catch (err) {
            notify('Erro ao atualizar contacto.');
        }
    };

    const handleDeleteContacto = async (id, nome) => {
        if (!await confirm(`Eliminar contacto "${nome}"?`)) return;
        try {
            await api.delete(`/contactos-uteis/${id}`);
            fetchContactosUteis();
        } catch (err) {
            notify('Erro ao eliminar contacto.');
        }
    };

    const handleUploadDoc = async (docName, file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('nome', docName);

        try {
            await api.post('/trabalhadores/documentos-internos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            fetchDocumentosInternos(); // Refresh list
            notify(`Documento "${docName}" carregado com sucesso!`);
        } catch (err) {
            console.error(err);
            notify('Erro ao carregar documento.');
        }
    };

    const REGISTO_FORMACAO_DOC = 'Registos de Formação/Ação de Acolhimento';

    const handleGerarRegistoFormacao = async () => {
        try {
            const res = await api.get('/trabalhadores/registo-formacao-acolhimento/pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Registo_Formacao_Acolhimento.pdf');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao gerar registo de formação:', error);
            notify('Erro ao gerar o documento.');
        }
    };

    const handleDownloadDoc = async (id, docName) => {
        try {
            const res = await api.get(`/trabalhadores/documentos-internos/${id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${docName.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao baixar documento:', error);
            notify('Erro ao baixar documento.');
        }
    };

    const loadData = async () => {
        try {
            const res = await api.get('/empresa');
            // Formatar datas para input date (YYYY-MM-DD)
            const data = res.data;
            const formatted = {};
            Object.keys(data).forEach(key => {
                if (key.includes('validade') || key.includes('emissao')) {
                    formatted[key] = data[key] ? data[key].split('T')[0] : '';
                } else {
                    formatted[key] = data[key];
                }
            });
            setFormData(formatted);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            notify('Erro ao carregar dados da empresa');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.put('/empresa', formData);
            await notify('Dados da empresa atualizados com sucesso!');
            onClose();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            notify('Erro ao salvar dados.');
        }
    };

    if (loading) return null;

    return (
        <OrcamentoDialog data-dashboard-dialog="true" label="Dados da Empresa" onClose={onClose}>
            <div className={theme === 'liquido' ? 'modal-content company-modal modal-ef' : 'modal-content company-modal'}>
                <div className={theme === 'liquido' ? 'modal-header modal-ef-header' : 'modal-header'}>
                    <h2><OrcamentoIcon name="settings" fallback="⚙️" /> Configuração da Empresa</h2>
                    <button className={theme === 'liquido' ? 'close-btn modal-close' : 'close-btn'} aria-label="Fechar" onClick={onClose}><OrcamentoIcon name="close" fallback="×" /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3>Identificação</h3>
                        <div className="form-row-3">
                            <div className="form-group">
                                <label>Nome da Empresa</label>
                                <input name="nome_empresa" value={formData.nome_empresa} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>NIF</label>
                                <input name="nif" value={formData.nif} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Alvará IMPIC</label>
                                <input name="alvara" value={formData.alvara} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Morada / Sede / Contacto</label>
                                <textarea name="morada" value={formData.morada} onChange={handleChange} rows="2" />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div className="form-group">
                                    <label>Email para Envio de Alertas</label>
                                    <input type="email" name="email_alertas" value={formData.email_alertas || ''} onChange={handleChange} placeholder="ex: alertas@sprod.pt" />
                                </div>
                                <div className="form-group">
                                    <label>Senha do Email</label>
                                    <div className="password-wrapper">
                                        <input 
                                            type={showEmailPass ? "text" : "password"} 
                                            name="email_alertas_pass" 
                                            value={formData.email_alertas_pass || ''} 
                                            onChange={handleChange} 
                                            placeholder="••••••••" 
                                        />
                                        <button 
                                            type="button" 
                                            className="password-toggle" 
                                            onClick={() => setShowEmailPass(!showEmailPass)}
                                            title={showEmailPass ? "Ocultar" : "Mostrar"}
                                        >
                                            {showEmailPass ? (
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', opacity: 0.8 }}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                            ) : (
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', opacity: 0.8 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <small style={{ gridColumn: 'span 2', marginTop: '-10px', color: '#718096', fontSize: '0.75rem' }}>
                                    Configuração para notificações automáticas do sistema.
                                </small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Projeto da Manutenção (despesas)</label>
                                <input name="projeto_manutencao" value={formData.projeto_manutencao || ''} onChange={handleChange} placeholder="ex: 17310" />
                                <small style={{ color: '#718096', fontSize: '0.75rem' }}>
                                    Nos cartões com o visto «Manutenção» (Trabalhadores → Cartões Associados), as compras marcadas como manutenção vão para este projeto.
                                </small>
                            </div>
                        </div>
                    </div>



                    <div className="form-section">
                        <h3>Seguros</h3>
                        <div className="form-row">
                            <div className="form-col">
                                <h4>Acidentes de Trabalho (AT)</h4>
                                <div className="form-group">
                                    <label>Seguradora</label>
                                    <input name="seguro_at_seguradora" value={formData.seguro_at_seguradora || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Apólice</label>
                                    <input name="seguro_at_apolice" value={formData.seguro_at_apolice || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Validade</label>
                                    <input type="date" name="seguro_at_validade" value={formData.seguro_at_validade || ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="form-col">
                                <h4>Responsabilidade Civil (RC)</h4>
                                <div className="form-group">
                                    <label>Seguradora</label>
                                    <input name="seguro_rc_seguradora" value={formData.seguro_rc_seguradora || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Apólice</label>
                                    <input name="seguro_rc_apolice" value={formData.seguro_rc_apolice || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Validade</label>
                                    <input type="date" name="seguro_rc_validade" value={formData.seguro_rc_validade || ''} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Declarações de Não Dívida</h3>
                        <div className="form-row">
                            <div className="form-col">
                                <h4>Finanças (AT)</h4>
                                <div className="form-group">
                                    <label>Data de Emissão</label>
                                    <input type="date" name="financas_data_emissao" value={formData.financas_data_emissao || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Validade</label>
                                    <input type="date" name="financas_validade" value={formData.financas_validade || ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="form-col">
                                <h4>Segurança Social</h4>
                                <div className="form-group">
                                    <label>Data de Emissão</label>
                                    <input type="date" name="ss_data_emissao" value={formData.ss_data_emissao || ''} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Validade</label>
                                    <input type="date" name="ss_validade" value={formData.ss_validade || ''} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Gestão de Documentos Internos (PDFs)</h3>
                        <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '15px' }}>
                            Carregue aqui os PDFs dos documentos obrigatórios. Estes documentos ficarão disponíveis para leitura e aceitação na ficha dos trabalhadores.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
                            {AVAILABLE_DOCS.map(docName => {
                                // Documento gerado automaticamente (não é upload)
                                if (docName === REGISTO_FORMACAO_DOC) {
                                    return (
                                        <div key={docName} className="company-document-card" style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #edf2f7', fontSize: '0.85rem' }}>
                                            <div style={{ fontWeight: '600', marginBottom: '5px' }}>{docName}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ color: '#718096', fontSize: '0.72rem' }}>
                                                    Gerado com os colaboradores aptos
                                                </span>
                                                <button type="button"
                                                    onClick={handleGerarRegistoFormacao}
                                                    style={{ cursor: 'pointer', background: '#805ad5', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', textDecoration: 'none' }}
                                                >
                                                    Gerar PDF
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }
                                const docInternal = documentosInternos.find(d => d.nome === docName);
                                return (
                                    <div key={docName} className="company-document-card" style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #edf2f7', fontSize: '0.85rem' }}>
                                        <div style={{ fontWeight: '600', marginBottom: '5px' }}>{docName}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span className="company-document-status" data-liquid-badge="true" data-liquid-tone={docInternal ? 'success' : 'warning'} style={{ color: docInternal ? '#48bb78' : '#e53e3e', fontSize: '0.75rem' }}>
                                                {docInternal ? `PDF v${docInternal.versao} (${new Date(docInternal.atualizado_em).toLocaleDateString()})` : 'Sem PDF'}
                                            </span>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                {docInternal && (
                                                    <button type="button"
                                                        onClick={() => handleDownloadDoc(docInternal.id, docName)}
                                                        style={{ cursor: 'pointer', background: '#48bb78', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', textDecoration: 'none' }}
                                                    >
                                                        Descarregar
                                                    </button>
                                                )}
                                                <label style={{ cursor: 'pointer', background: '#3182ce', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', margin: 0 }}>
                                                    Carregar
                                                    <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={(e) => handleUploadDoc(docName, e.target.files[0])} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* FICHAS TÉCNICAS */}
                    <div className="form-section">
                        <h3>Fichas Técnicas (PDF)</h3>
                        {/* As fichas dos produtos passaram a viver no SGI, ligadas ao produto
                            que os trabalhadores registam — carregar aqui já não chegava a ninguém. */}
                        <div data-liquid-notice="true" data-liquid-tone="info" style={{ fontSize: '0.85rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', marginBottom: '15px', lineHeight: 1.45 }}>
                            As fichas técnicas e as FDS dos produtos químicos passaram para <b>Sistema Integrado → Req. Legais → pasta FDS</b>.
                            Os trabalhadores consultam-nas em <b>Produtos químicos</b>, no telemóvel.
                        </div>
                        {fichasTecnicas.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                {fichasTecnicas.map(ft => (
                                    <div key={ft.id} className="company-document-card" style={{ background: '#fff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ fontWeight: '700', color: '#2d3748', lineHeight: '1.3', wordBreak: 'break-word' }}>{ft.nome}</div>
                                        <div style={{ color: '#48bb78', fontSize: '0.75rem' }}>
                                            PDF v{ft.versao} · {new Date(ft.atualizado_em).toLocaleDateString()}
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            <button type="button" onClick={() => handleDownloadFichaTecnica(ft.id, ft.nome)} style={{ cursor: 'pointer', background: '#48bb78', color: 'white', padding: '5px 10px', borderRadius: '5px', fontSize: '0.75rem', fontWeight: '600', textDecoration: 'none' }}>Descarregar</button>
                                            <button type="button" onClick={() => handleDeleteFichaTecnica(ft.id, ft.nome)} style={{ background: '#fed7d7', color: '#c53030', border: 'none', padding: '5px 10px', borderRadius: '5px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}><DeleteIcon /> Eliminar</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* CONTACTOS ÚTEIS */}
                    <div className="form-section">
                        <h3>Contactos Úteis</h3>
                        <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '15px' }}>
                            Contactos de acesso rápido para os utilizadores mobile.
                        </p>
                        {contactosUteis.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                                {contactosUteis.map(c => (
                                    <div key={c.id} className="company-contact-card" style={{ background: '#fff', padding: '10px 12px', borderRadius: '6px', border: '1px solid #edf2f7', fontSize: '0.85rem' }}>
                                        {editingContacto?.id === c.id ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <input value={editingContacto.nome} onChange={e => setEditingContacto(p => ({ ...p, nome: e.target.value }))} placeholder="Nome" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                                <input value={editingContacto.cargo} onChange={e => setEditingContacto(p => ({ ...p, cargo: e.target.value }))} placeholder="Cargo / Função" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                                <input value={editingContacto.telefone} onChange={e => setEditingContacto(p => ({ ...p, telefone: e.target.value }))} placeholder="Telefone" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                                <input value={editingContacto.email} onChange={e => setEditingContacto(p => ({ ...p, email: e.target.value }))} placeholder="Email" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button type="button" onClick={handleUpdateContacto} style={{ background: '#48bb78', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' }}>Guardar</button>
                                                    <button type="button" onClick={() => setEditingContacto(null)} style={{ background: '#edf2f7', color: '#4a5568', border: 'none', padding: '5px 12px', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' }}>Cancelar</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ fontWeight: '700', marginBottom: '2px' }}>{c.nome}</div>
                                                {c.cargo && <div style={{ color: '#718096', fontSize: '0.78rem' }}>{c.cargo}</div>}
                                                {c.telefone && <div style={{ color: '#3182ce', fontSize: '0.82rem', fontWeight: '600' }}>{c.telefone}</div>}
                                                {c.email && <div style={{ color: '#718096', fontSize: '0.78rem' }}>{c.email}</div>}
                                                <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                                                    <button type="button" onClick={() => setEditingContacto({ ...c })} style={{ background: '#bee3f8', color: '#2b6cb0', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}>Editar</button>
                                                    <button type="button" onClick={() => handleDeleteContacto(c.id, c.nome)} style={{ background: '#fed7d7', color: '#c53030', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}><DeleteIcon /> Eliminar</button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.85rem', marginBottom: '10px', color: '#4a5568' }}>Novo Contacto</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <input value={novoContacto.nome} onChange={e => setNovoContacto(p => ({ ...p, nome: e.target.value }))} placeholder="Nome *" style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem', gridColumn: 'span 2' }} />
                                <input value={novoContacto.cargo} onChange={e => setNovoContacto(p => ({ ...p, cargo: e.target.value }))} placeholder="Cargo / Função" style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                <input value={novoContacto.telefone} onChange={e => setNovoContacto(p => ({ ...p, telefone: e.target.value }))} placeholder="Telefone" style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }} />
                                <input value={novoContacto.email} onChange={e => setNovoContacto(p => ({ ...p, email: e.target.value }))} placeholder="Email" style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem', gridColumn: 'span 2' }} />
                            </div>
                            <button type="button" onClick={handleSaveContacto} style={{ marginTop: '10px', background: '#3182ce', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '600' }}>
                                + Adicionar Contacto
                            </button>
                        </div>
                    </div>

                    <div className={theme === 'liquido' ? 'modal-actions modal-form-actions' : 'modal-actions'}>
                        <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn-primary">Salvar Configuração</button>
                    </div>
                </form>
            </div>
        </OrcamentoDialog>
    );
};

export default CompanySettingsModal;
